// Home dashboard: the quote banner (one per page load), pastel gradient
// metric cards (v_revenue_metrics), the retention machine — a three-column
// 🟢🟡🔴 board over v_retention plus THE QUEUE (one big card at a time:
// AI-drafted text in up to three Option tabs, Send/Edit/Skip/Snooze) — the
// pending-reminders list (client_reminders, complete/dismiss via the
// complete_reminder RPC), and the active-packages list (packages +
// package_ledger; balance is always SUM(ledger delta)).
//
// All numbers are computed views/ledger sums — nothing here stores metrics
// (SPEC §0), and retention colors are derived, never stored. Display dollars
// respect privacy mode (lib/privacy.ts) — the 💳 credit indicator never does.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { runAiNow, sendSms, draftFor, API_URL } from "../lib/api";
import { extractOfferAmount, hasOfferLanguage } from "../lib/offers";
import { money } from "../lib/format";
import { maskMoney, usePrivacy } from "../lib/privacy";
import { fmtDate } from "../lib/dates";
import {
  RETENTION_COLORS,
  RETENTION_EMOJI,
  RETENTION_LABELS,
} from "../lib/retention";
import MarkReachedOut from "../components/MarkReachedOut";
import QuoteBanner from "../components/QuoteBanner";
import { RetentionDot, WhaleBadge } from "../components/Badges";
import type {
  AiRecommendation,
  Id,
  Metrics,
  Reminder,
  RetentionColor,
  RetentionRow,
} from "../lib/types";

/** Today's business date (YYYY-MM-DD, America/New_York). */
function nyTodayYmd(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** PostgREST can serialize numerics as strings; normalize before math. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isNaN(n) ? null : n;
}

/** clients.id is integer in the live DB; send a number when the value is numeric. */
function toIdArg(v: Id): Id {
  return typeof v === "string" && /^\d+$/.test(v) ? Number(v) : v;
}

interface ContactStub {
  id: Id;
  full_name: string;
  phone_e164: string | null;
  sms_opt_out: boolean;
  rebook_snoozed_until: string | null;
}

/** An active package row plus its computed ledger balance. */
interface HomePackage {
  id: Id;
  client_id: Id;
  drips_total: number;
  /** SUM(package_ledger.delta) — computed, never stored. */
  balance: number;
}

interface PackagesHomeData {
  packages: HomePackage[];
  /** client_id (stringified) → display name, incl. fallbacks for clients not in v_client_list. */
  names: Record<string, string>;
}

/** One card in THE QUEUE: an AI pick and/or a red/yellow retention client. */
interface QueueCard {
  key: string;
  clientId: Id;
  name: string;
  rec: AiRecommendation | null;
  ret: RetentionRow | null;
}

/**
 * The current card's draft options (Option 1/2/3 tabs over the textarea).
 * `base` holds the pristine texts as the worker returned them; `texts` holds
 * what's editable — unsaved edits live per option, so switching tabs and back
 * preserves them. The textarea always shows texts[active].
 */
interface DraftOptions {
  base: string[];
  texts: string[];
  active: number;
}

function optionsFrom(texts: string[]): DraftOptions {
  return { base: [...texts], texts: [...texts], active: 0 };
}

/**
 * Swap in a freshly fetched variant set while keeping any per-option edits
 * the operator already typed (an option counts as edited when its text
 * differs from its old pristine base). Used when the full trio arrives behind
 * an instantly-rendered pre-stored rec draft.
 */
function mergeVariants(prev: DraftOptions | null, fetched: string[]): DraftOptions {
  if (!prev) return optionsFrom(fetched);
  const texts = [...fetched];
  for (let i = 0; i < Math.min(prev.texts.length, texts.length); i++) {
    if (prev.texts[i] !== prev.base[i]) texts[i] = prev.texts[i];
  }
  return {
    base: [...fetched],
    texts,
    active: Math.min(prev.active, Math.max(texts.length - 1, 0)),
  };
}

/** Split `xs` into slices of at most `size` (PostgREST .in() lives in the URL). */
function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

const CONFETTI_COLORS = [
  "var(--mint)",
  "var(--sky)",
  "var(--rose)",
  "var(--butter)",
  "var(--lavender)",
  "var(--peach)",
  "var(--accent)",
];

/** Tiny CSS-only confetti burst; remounts (and replays) on every `burst` bump. */
function ConfettiBurst({ burst }: { burst: number }) {
  const pieces = useMemo(() => {
    if (burst === 0) return [];
    return Array.from({ length: 22 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 22 + Math.random() * 0.6;
      const dist = 60 + Math.random() * 110;
      return {
        dx: `${Math.round(Math.cos(angle) * dist)}px`,
        dy: `${Math.round(Math.sin(angle) * dist - 40)}px`,
        rot: `${Math.round(Math.random() * 540 - 270)}deg`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${Math.round(Math.random() * 90)}ms`,
      };
    });
  }, [burst]);
  if (burst === 0) return null;
  return (
    <div className="confetti" key={burst} aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={
            {
              background: p.color,
              animationDelay: p.delay,
              "--dx": p.dx,
              "--dy": p.dy,
              "--rot": p.rot,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

export default function Home() {
  const today = nyTodayYmd();
  // Privacy mode (MetricsBar 👁 toggle): display dollars mask to "$ ••••".
  // NEVER applied to the 💳 credit indicator or the draft text (operational).
  const [privacy] = usePrivacy();

  const metricsQ = useQuery<Metrics>(async () => {
    const { data, error } = await supabase.from("v_revenue_metrics").select("*").single();
    if (error) throw new Error(error.message);
    return data as Metrics;
  }, []);

  const recsQ = useQuery<AiRecommendation[]>(async () => {
    const { data, error } = await supabase
      .from("ai_recommendations")
      .select("*")
      .eq("run_date", today)
      .eq("status", "active")
      .order("rank", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as AiRecommendation[];
  }, [today]);

  const retentionQ = useQuery<RetentionRow[]>(async () => {
    const { data, error } = await supabase.from("v_retention").select("*");
    if (error) throw new Error(error.message);
    return (data ?? []) as RetentionRow[];
  }, []);

  const remindersQ = useQuery<Reminder[]>(async () => {
    const { data, error } = await supabase
      .from("client_reminders")
      .select("*")
      .eq("status", "pending")
      .order("due_date", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as Reminder[];
  }, []);

  // Name/phone/snooze lookup for AI recs + reminders (v_retention carries its own).
  const contactsQ = useQuery<ContactStub[]>(async () => {
    const { data, error } = await supabase
      .from("v_client_list")
      .select("id, full_name, phone_e164, sms_opt_out, rebook_snoozed_until");
    if (error) throw new Error(error.message);
    return (data ?? []) as ContactStub[];
  }, []);

  // Active packages + ledger balances + owning-client names, self-contained so
  // it can reload independently (e.g. after an AI run files a new package).
  const packagesQ = useQuery<PackagesHomeData>(async () => {
    const { data, error } = await supabase
      .from("packages")
      .select("id, client_id, drips_total")
      .eq("active", true);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as { id: Id; client_id: Id; drips_total: number }[];
    if (rows.length === 0) return { packages: [], names: {} };

    const balances = new Map<string, number>();
    for (const ids of chunk(rows.map((p) => p.id), 100)) {
      const led = await supabase
        .from("package_ledger")
        .select("package_id, delta")
        .in("package_id", ids);
      if (led.error) throw new Error(led.error.message);
      for (const l of (led.data ?? []) as { package_id: Id; delta: number }[]) {
        const k = String(l.package_id);
        balances.set(k, (balances.get(k) ?? 0) + Number(l.delta ?? 0));
      }
    }

    const names: Record<string, string> = {};
    const clientIds = [...new Set(rows.map((p) => String(p.client_id)))];
    for (const ids of chunk(clientIds, 100)) {
      const res = await supabase.from("v_client_list").select("id, full_name").in("id", ids);
      if (res.error) throw new Error(res.error.message);
      for (const c of (res.data ?? []) as { id: Id; full_name: string }[]) {
        names[String(c.id)] = c.full_name;
      }
    }
    // Internal / no-longer-listed clients don't appear in v_client_list —
    // fall back to the raw clients table so their packages still show a name.
    const missing = clientIds.filter((cid) => !(cid in names));
    for (const ids of chunk(missing, 100)) {
      const res = await supabase
        .from("clients")
        .select("id, first_name, last_name")
        .in("id", ids);
      if (res.error) throw new Error(res.error.message);
      for (const c of (res.data ?? []) as {
        id: Id;
        first_name: string | null;
        last_name: string | null;
      }[]) {
        names[String(c.id)] =
          [c.first_name, c.last_name].filter(Boolean).join(" ") || `Client ${String(c.id)}`;
      }
    }

    const packages: HomePackage[] = rows.map((p) => ({
      ...p,
      balance: balances.get(String(p.id)) ?? 0,
    }));
    // Low balances first, then by name so ties are stable.
    packages.sort(
      (a, b) =>
        a.balance - b.balance ||
        (names[String(a.client_id)] ?? "").localeCompare(names[String(b.client_id)] ?? ""),
    );
    return { packages, names };
  }, []);

  const contacts = useMemo(() => {
    const m = new Map<string, ContactStub>();
    for (const c of contactsQ.data ?? []) m.set(String(c.id), c);
    return m;
  }, [contactsQ.data]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  /** After any logged touch: cooldown flags changed, recs may be done. */
  function reloadTouchLists() {
    recsQ.reload();
    retentionQ.reload();
    remindersQ.reload(); // a touch can create a follow-up reminder
  }

  async function completeReminder(rem: Reminder, status: "done" | "dismissed") {
    setBusyId(rem.id);
    setActionError(null);
    try {
      const { error } = await supabase.rpc("complete_reminder", {
        p_id: rem.id,
        p_status: status,
      });
      if (error) throw new Error(error.message);
      remindersQ.reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function runAi() {
    setAiBusy(true);
    setActionError(null);
    setAiNotice(null);
    try {
      const res = await runAiNow();
      // Package counts only exist on newer workers; mention them when present.
      const pkgBits: string[] = [];
      if (res.packages_created) {
        pkgBits.push(
          `${res.packages_created} package${res.packages_created === 1 ? "" : "s"} filed`,
        );
      }
      if (res.package_flags) {
        pkgBits.push(
          `${res.package_flags} package flag${res.package_flags === 1 ? "" : "s"} for review`,
        );
      }
      const pkgSuffix = pkgBits.length > 0 ? `, ${pkgBits.join(", ")}` : "";
      if (res.skipped) {
        // `skipped` is the worker's reason string (e.g. "ANTHROPIC_API_KEY not set").
        setAiNotice(`AI run skipped: ${res.skipped}`);
      } else if (res.errors && res.errors.length > 0) {
        setAiNotice(
          `AI run finished with errors — ${res.recommendations} recommendations, ` +
            `${res.reminders_created} reminders${pkgSuffix}. ${res.errors.join("; ")}`,
        );
      } else {
        setAiNotice(
          `AI run finished — ${res.recommendations} recommendation${
            res.recommendations === 1 ? "" : "s"
          }, ${res.reminders_created} reminder${res.reminders_created === 1 ? "" : "s"} created` +
            `${pkgSuffix}.`,
        );
      }
      recsQ.reload();
      remindersQ.reload();
      retentionQ.reload();
      packagesQ.reload(); // the extractor may have filed new packages
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  }

  const recs = recsQ.data ?? [];
  const retention = retentionQ.data ?? [];
  const reminders = remindersQ.data ?? [];
  const homePkgs = packagesQ.data?.packages ?? [];
  const pkgNames = packagesQ.data?.names ?? {};

  const retMap = useMemo(() => {
    const m = new Map<string, RetentionRow>();
    for (const r of retention) m.set(String(r.client_id), r);
    return m;
  }, [retention]);

  // ---- RETENTION BOARD ----
  const byMonetaryDesc = (a: RetentionRow, b: RetentionRow) =>
    (num(b.monetary) ?? 0) - (num(a.monetary) ?? 0);

  const board = useMemo(
    () =>
      RETENTION_COLORS.map((color) => {
        const all = retention.filter((r) => r.retention_color === color);
        const active = all.filter((r) => !r.in_cooldown).sort(byMonetaryDesc);
        return {
          color,
          active,
          cooling: all.length - active.length,
          /** Active + cooling — what /clients?color=<c> will actually list. */
          total: all.length,
          sum: active.reduce((s, r) => s + (num(r.monetary) ?? 0), 0),
        };
      }),
    [retention],
  );

  // ---- THE QUEUE ----
  // Cards handled this session (sent / skipped / snoozed) never come back,
  // even across reloads — the DB writes catch up on the next full refresh.
  const [handled, setHandled] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [sentCount, setSentCount] = useState(0);
  const [sentMonetary, setSentMonetary] = useState(0);
  const [confettiBurst, setConfettiBurst] = useState(0);

  const queue = useMemo<QueueCard[]>(() => {
    const cards: QueueCard[] = [];
    const seen = new Set<string>();

    const eligible = (cid: string, ret: RetentionRow | null): boolean => {
      const c = contacts.get(cid);
      const phone = ret?.phone_e164 ?? c?.phone_e164 ?? null;
      const optOut = ret?.sms_opt_out ?? c?.sms_opt_out ?? false;
      const snoozed = c?.rebook_snoozed_until != null && c.rebook_snoozed_until >= today;
      return phone != null && !optOut && !snoozed && !(ret?.in_cooldown ?? false);
    };

    // 1) Today's AI picks, by rank (they carry pre-written drafts).
    for (const rec of recs) {
      const cid = String(rec.client_id);
      if (seen.has(cid)) continue;
      seen.add(cid);
      const ret = retMap.get(cid) ?? null;
      if (!eligible(cid, ret)) continue;
      cards.push({
        key: `rec-${rec.id}`,
        clientId: rec.client_id,
        // v_retention.full_name can be NULL — never let "null" reach the card.
        name: ret?.full_name ?? contacts.get(cid)?.full_name ?? "this client",
        rec,
        ret,
      });
    }

    // 2) Reds by lifetime value desc, then 3) yellows.
    for (const color of ["red", "yellow"] as RetentionColor[]) {
      for (const r of retention.filter((x) => x.retention_color === color).sort(byMonetaryDesc)) {
        const cid = String(r.client_id);
        if (seen.has(cid)) continue;
        seen.add(cid);
        if (!eligible(cid, r)) continue;
        cards.push({
          key: `ret-${cid}`,
          clientId: r.client_id,
          name: r.full_name ?? contacts.get(cid)?.full_name ?? "this client",
          rec: null,
          ret: r,
        });
      }
    }

    return cards.filter((c) => !handled.has(c.key));
  }, [recs, retention, retMap, contacts, handled, today]);

  const current = queue.length > 0 ? queue[0] : null;

  // ---- draft loading for the current card ----
  // A variant's offer_amount is only the INTENT that shaped its text; the
  // credit actually filed (and the 💳 indicator) is always parsed live from
  // the textarea by lib/offers.ts — so it is never tracked as state here.
  const [options, setOptions] = useState<DraftOptions | null>(null);
  // Full-card shimmer: no text to show yet at all.
  const [drafting, setDrafting] = useState(false);
  // Background variant fetch (behind a visible pre-draft, or "↻ New options").
  const [loadingMore, setLoadingMore] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [lastDrip, setLastDrip] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  // Session variant cache keyed by client id: Skip → later return re-uses the
  // fetched trio instead of re-billing the model (the worker additionally
  // persists on-demand drafts onto today's ai_recommendations row).
  const draftCache = useRef(new Map<string, string[]>());
  const dripCache = useRef(new Map<string, string | null>());
  // Which card is showing right now — guards async callbacks that outlive it.
  const currentKeyRef = useRef<string | null>(null);

  useEffect(() => {
    currentKeyRef.current = current?.key ?? null;
    setDraftError(null);
    setLoadingMore(false);
    if (!current) {
      setOptions(null);
      setLastDrip(null);
      return;
    }
    const cid = String(current.clientId);
    let cancelled = false;

    // Last drip name (most recent sale WITH a service_name), cached per client.
    const cachedDrip = dripCache.current.get(cid);
    if (cachedDrip !== undefined) {
      setLastDrip(cachedDrip);
    } else {
      setLastDrip(null);
      void (async () => {
        const { data } = await supabase
          .from("sales")
          .select("service_name")
          .eq("client_id", toIdArg(current.clientId))
          .not("service_name", "is", null)
          .order("sale_date", { ascending: false })
          .limit(1);
        const name = ((data ?? [])[0]?.service_name as string | undefined) ?? null;
        dripCache.current.set(cid, name);
        if (!cancelled) setLastDrip(name);
      })();
    }

    // Session cache wins (already-fetched trio); then the rec pre-draft shows
    // instantly as Option 1 while the trio loads behind it; then POST /ai/draft.
    const cached = draftCache.current.get(cid);
    if (cached !== undefined) {
      setOptions(optionsFrom(cached));
      setDrafting(false);
      return () => {
        cancelled = true;
      };
    }

    const preDraft = current.rec?.draft;
    if (preDraft) {
      setOptions(optionsFrom([preDraft]));
      setDrafting(false);
      if (!API_URL) {
        return () => {
          cancelled = true;
        };
      }
      setLoadingMore(true);
      draftFor(current.clientId).then(
        (d) => {
          const texts = d.variants.map((v) => v.text);
          draftCache.current.set(cid, texts);
          if (cancelled) return;
          setLoadingMore(false);
          // Keep any edits already typed into the visible pre-draft.
          setOptions((prev) => mergeVariants(prev, texts));
        },
        () => {
          // Best-effort upgrade — the pre-draft stays perfectly sendable.
          if (!cancelled) setLoadingMore(false);
        },
      );
      return () => {
        cancelled = true;
      };
    }

    if (!API_URL) {
      setDraftError("VITE_CRM_API_URL is not configured — write the text yourself");
      setOptions(optionsFrom([""]));
      return () => {
        cancelled = true;
      };
    }
    setOptions(null);
    setDrafting(true);
    draftFor(current.clientId).then(
      (d) => {
        const texts = d.variants.map((v) => v.text);
        draftCache.current.set(cid, texts);
        if (cancelled) return;
        setOptions(optionsFrom(texts));
        setDrafting(false);
      },
      (e: unknown) => {
        if (cancelled) return;
        setDraftError(e instanceof Error ? e.message : String(e));
        setOptions(optionsFrom([""])); // blank canvas — write it yourself
        setDrafting(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key]);

  /** The textarea's backing text: the active option, edits included. */
  const draftText = options ? (options.texts[options.active] ?? "") : "";

  function editActive(text: string) {
    setOptions((prev) => {
      if (!prev) return prev;
      const texts = [...prev.texts];
      texts[prev.active] = text;
      return { ...prev, texts };
    });
  }

  /** "↻ New options" — re-draft a fresh set, replacing the current options. */
  function refreshOptions() {
    const card = current;
    if (!card || drafting || loadingMore || sending || !API_URL) return;
    const key = card.key;
    const cid = String(card.clientId);
    setDraftError(null);
    setLoadingMore(true);
    draftFor(card.clientId).then(
      (d) => {
        const texts = d.variants.map((v) => v.text);
        draftCache.current.set(cid, texts);
        if (currentKeyRef.current !== key) return;
        setLoadingMore(false);
        setOptions(optionsFrom(texts)); // an explicit refresh replaces edits
      },
      (e: unknown) => {
        if (currentKeyRef.current !== key) return;
        setLoadingMore(false);
        setDraftError(e instanceof Error ? e.message : String(e));
      },
    );
  }

  function advance(card: QueueCard) {
    setHandled((prev) => {
      const next = new Set(prev);
      next.add(card.key);
      return next;
    });
  }

  async function sendCurrent() {
    const card = current;
    if (!card || sending || drafting) return;
    const text = draftText.trim();
    if (!text) return;

    // Bidirectional offer guard (lib/offers.ts): the credit filed always
    // equals the dollar promise in the FINAL text — whatever the draft's
    // offer_amount intent was. Offer language with an unreadable amount must
    // be confirmed so a promise never goes out silently unrecorded.
    const offerAmt = extractOfferAmount(text);
    if (offerAmt == null && hasOfferLanguage(text)) {
      const sendAnyway = window.confirm(
        "Your text seems to promise a discount but I can't read the amount — " +
          "send WITHOUT filing a credit, or edit the text?\n\n" +
          "OK = Send anyway (no credit) · Cancel = Keep editing",
      );
      if (!sendAnyway) return;
    }

    setSending(true);
    setQueueError(null);
    let smsSent = false;
    try {
      // Send via the worker; outbound messages count as touches automatically
      // (v_retention/v_outreach_today) — NO outreach_touches insert here.
      await sendSms(toIdArg(card.clientId), text);
      smsSent = true;

      // File the credit the FINAL text promises — exactly the parsed amount.
      if (offerAmt != null) {
        const { error } = await supabase.from("client_credits").insert({
          client_id: toIdArg(card.clientId),
          amount: offerAmt,
          reason: "outreach offer — " + text.slice(0, 80),
          expires_on: addDaysYmd(today, 30),
        });
        if (error) {
          throw new Error(
            `Message sent, but filing the $${offerAmt} credit failed: ${error.message}`,
          );
        }
      }

      if (card.rec) {
        const { error } = await supabase
          .from("ai_recommendations")
          .update({ status: "done" })
          .eq("id", card.rec.id);
        if (error) {
          throw new Error(`Message sent, but marking the AI pick done failed: ${error.message}`);
        }
      }
    } catch (e) {
      setQueueError(e instanceof Error ? e.message : String(e));
      if (!smsSent) {
        setSending(false);
        return;
      }
      // The text went out — count it and advance despite the follow-up failure.
    }
    setSentCount((c) => c + 1);
    setSentMonetary((v) => v + (num(card.ret?.monetary) ?? 0));
    setConfettiBurst((k) => k + 1);
    advance(card);
    retentionQ.reload(); // the send flips their cooldown → board counts move
    setSending(false);
  }

  function skipCurrent() {
    if (!current || sending) return;
    setQueueError(null);
    advance(current);
  }

  async function snoozeCurrent() {
    const card = current;
    if (!card || sending) return;
    setSending(true);
    setQueueError(null);
    try {
      // Snooze must NOT fake a touch — no outreach_touches row. Just push
      // rebook_snoozed_until out 45 days (the permitted clients update).
      const { error } = await supabase
        .from("clients")
        .update({ rebook_snoozed_until: addDaysYmd(today, 45) })
        .eq("id", toIdArg(card.clientId));
      if (error) throw new Error(error.message);
      advance(card);
      contactsQ.reload(); // snooze is read from v_client_list
    } catch (e) {
      setQueueError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  function textButton(clientId: Id, fallbackPhone?: string | null) {
    const c = contacts.get(String(clientId));
    const phone = c?.phone_e164 ?? fallbackPhone ?? null;
    if (!phone || c?.sms_opt_out) return null;
    return (
      <Link className="btn btn-primary btn-sm" to={`/clients/${clientId}?compose=1`}>
        Text
      </Link>
    );
  }

  function nameOf(clientId: Id, fallback?: string | null): string {
    return contacts.get(String(clientId))?.full_name ?? fallback ?? `Client ${String(clientId)}`;
  }

  const m = metricsQ.data;
  const fmt = (f: (mm: Metrics) => string): string => (m ? f(m) : "…");
  /** Dollar metric-card values go through the privacy mask; counts don't. */
  const fmtMoney = (f: (mm: Metrics) => number | string | null): string =>
    m ? maskMoney(money(f(m)), privacy) : "…";

  // Meta line for the current queue card:
  // "$X lifetime · every ~N weeks · quiet M weeks · last drip <name>"
  const cardMeta = useMemo(() => {
    if (!current) return "";
    const ret = current.ret;
    const bits: string[] = [];
    const monetary = num(ret?.monetary);
    if (monetary != null) bits.push(`${maskMoney(money(monetary), privacy)} lifetime`);
    const gap = num(ret?.median_gap_days);
    if (gap != null && gap > 0) {
      const w = Math.max(1, Math.round(gap / 7));
      bits.push(`every ~${w} week${w === 1 ? "" : "s"}`);
    }
    const rec = num(ret?.recency_days);
    if (rec != null) {
      const w = Math.floor(rec / 7);
      bits.push(`quiet ${w} week${w === 1 ? "" : "s"}`);
    }
    if (lastDrip) bits.push(`last drip ${lastDrip}`);
    return bits.join(" · ");
  }, [current, lastDrip, privacy]);

  // Live offer indicator — driven purely off the textarea (any color, any
  // source): present exactly when sending right now would file a credit.
  const liveOffer = useMemo(() => extractOfferAmount(draftText), [draftText]);

  // Don't build/show the current card until the supporting contact data
  // (phones, opt-outs, snoozes) has loaded — otherwise snoozed/opted-out
  // clients transiently appear and the card can swap mid-edit once the
  // eligibility data arrives.
  const queueLoading =
    recsQ.loading ||
    (retentionQ.loading && !retentionQ.data) ||
    (contactsQ.loading && !contactsQ.data);

  return (
    <div className="page">
      <QuoteBanner />
      <div className="page-header">
        <div>
          <h1 className="page-title">Home</h1>
          <div className="page-sub">Today at Fresh IV Drips — {fmtDate(today)}</div>
        </div>
        <div className="page-actions">
          {API_URL && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void runAi()}
              disabled={aiBusy}
              title="Regenerate today's AI outreach recommendations"
            >
              {aiBusy ? "Running AI…" : "Run AI now"}
            </button>
          )}
        </div>
      </div>

      {metricsQ.error && (
        <div className="error-banner">Failed to load metrics: {metricsQ.error}</div>
      )}
      {actionError && <div className="error-banner">{actionError}</div>}
      {aiNotice && <div className="info-banner">{aiNotice}</div>}

      <div className="home-metric-grid">
        <div className="metric-card grad-mint">
          <div className="metric-card-label">Today</div>
          <div className="metric-card-value">{fmtMoney((x) => x.today_revenue)}</div>
          <div className="metric-card-sub">collected</div>
        </div>
        <div className="metric-card grad-sky">
          <div className="metric-card-label">This week</div>
          <div className="metric-card-value">{fmtMoney((x) => x.week_revenue)}</div>
          <div className="metric-card-sub">ISO week, Mon–Sun (NY)</div>
        </div>
        <div className="metric-card grad-lavender">
          <div className="metric-card-label">This month</div>
          <div className="metric-card-value">{fmtMoney((x) => x.month_revenue)}</div>
          <div className="metric-card-sub">calendar month</div>
        </div>
        <div className="metric-card grad-butter">
          <div className="metric-card-label">All-time</div>
          <div className="metric-card-value">{fmtMoney((x) => x.alltime_revenue)}</div>
          <div className="metric-card-sub">
            {m ? `${maskMoney(money(m.alltime_refunds), privacy)} refunds` : "…"}
          </div>
        </div>
        <div className="metric-card grad-peach">
          <div className="metric-card-label">Paying clients</div>
          <div className="metric-card-value">{fmt((x) => String(x.paying_clients))}</div>
          <div className="metric-card-sub">{m ? `${m.total_clients} total` : "…"}</div>
        </div>
        <div className="metric-card grad-rose">
          <div className="metric-card-label">Avg LTV</div>
          <div className="metric-card-value">{fmtMoney((x) => x.avg_ltv)}</div>
          <div className="metric-card-sub">per paying client</div>
        </div>
      </div>

      <div className="home-section">
        <div className="panel">
          <div className="panel-title">Retention board</div>
          {retentionQ.error && (
            <div className="error-banner">
              Failed to load the retention board: {retentionQ.error}
            </div>
          )}
          {retentionQ.loading && !retentionQ.data && (
            <div className="loading">
              <span className="spinner" /> Loading retention board…
            </div>
          )}
          {retentionQ.data && (
            <div className="retention-board">
              {board.map(({ color, active, cooling, total, sum }) => (
                <div key={color} className={`retention-col col-${color}`}>
                  <div className="retention-col-head">
                    <span aria-hidden="true">{RETENTION_EMOJI[color]}</span>
                    <span className="retention-col-label">{RETENTION_LABELS[color]}</span>
                    <span className="retention-col-count">{active.length}</span>
                    {cooling > 0 && (
                      <span
                        className="retention-cooling"
                        title="Recently contacted — in the outreach cooldown window"
                      >
                        +{cooling} cooling
                      </span>
                    )}
                  </div>
                  <div className="retention-col-sum">
                    {maskMoney(money(sum), privacy)} lifetime at risk
                  </div>
                  <div className="retention-rows">
                    {active.slice(0, 8).map((r) => {
                      const quietW = num(r.recency_days);
                      return (
                        <Link
                          key={String(r.client_id)}
                          className={"retention-row" + (r.sms_opt_out ? " opted-out" : "")}
                          to={`/clients/${r.client_id}`}
                          title={
                            r.sms_opt_out
                              ? `${r.full_name ?? "this client"} — SMS opt-out`
                              : undefined
                          }
                        >
                          <RetentionDot color={color} />
                          <span className="retention-row-name">
                            {r.full_name ?? "this client"}
                            {r.is_whale ? " 🐋" : ""}
                          </span>
                          <span className="retention-row-meta">
                            {maskMoney(money(num(r.monetary) ?? 0), privacy)}
                            {quietW != null ? ` · ${Math.floor(quietW / 7)}w` : ""}
                          </span>
                        </Link>
                      );
                    })}
                    {active.length === 0 && (
                      <div className="muted small">Nobody here right now.</div>
                    )}
                  </div>
                  {total > 8 && (
                    // Count = active + cooling, because /clients?color=<c>
                    // filters on the derived color only and lists both.
                    <Link className="retention-show-all" to={`/clients?color=${color}`}>
                      Show all {total} →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="home-section">
        <div className="panel">
          <div className="panel-title">The queue</div>
          <div className="queue-counter">
            <span>
              {sentCount} sent today · {maskMoney(money(sentMonetary), privacy)} lifetime value
              touched
            </span>
            {!queueLoading && queue.length > 0 && (
              <span className="muted">· {queue.length} in the queue</span>
            )}
          </div>
          {recsQ.error && (
            <div className="error-banner">Failed to load AI recommendations: {recsQ.error}</div>
          )}
          {queueError && <div className="error-banner">{queueError}</div>}
          {queueLoading && (
            <div className="loading">
              <span className="spinner" /> Building the queue…
            </div>
          )}
          {!queueLoading && (
            <div style={{ position: "relative" }}>
              {current ? (
                <div className="queue-card">
                  <div className="queue-client">
                    <RetentionDot color={current.ret?.retention_color ?? null} />
                    <Link className="queue-name" to={`/clients/${current.clientId}`}>
                      {current.name}
                    </Link>
                    <WhaleBadge whale={current.ret?.is_whale} />
                  </div>
                  {cardMeta && <div className="queue-meta">{cardMeta}</div>}
                  {current.rec && (
                    <div className="queue-reason">
                      AI pick #{current.rec.rank} — {current.rec.reason}
                    </div>
                  )}
                  {draftError && (
                    <div className="error-banner mt-1">
                      Drafting failed: {draftError}. Write the text yourself, or skip.
                    </div>
                  )}
                  {!drafting && options && (options.base.length > 1 || loadingMore || !!API_URL) && (
                    <div className="draft-tabs" role="tablist" aria-label="Draft options">
                      {options.base.length > 1 &&
                        options.texts.map((t, i) => (
                          <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={i === options.active}
                            className={"draft-tab" + (i === options.active ? " active" : "")}
                            onClick={() => setOptions((o) => (o ? { ...o, active: i } : o))}
                            disabled={sending}
                          >
                            Option {i + 1}
                            {extractOfferAmount(t) != null && (
                              <span
                                className="draft-tab-credit"
                                title="This option promises a dollar credit — sending it files one"
                              >
                                💳
                              </span>
                            )}
                          </button>
                        ))}
                      {loadingMore && <span className="draft-tabs-hint">drafting options…</span>}
                      <span className="spacer" />
                      {API_URL && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={refreshOptions}
                          disabled={sending || loadingMore}
                          title="Generate a fresh set of drafts (replaces these options)"
                        >
                          ↻ New options
                        </button>
                      )}
                    </div>
                  )}
                  {drafting ? (
                    <div className="shimmer" role="status" aria-label="Writing the draft…">
                      <div className="shimmer-line" />
                      <div className="shimmer-line" />
                      <div className="shimmer-line short" />
                    </div>
                  ) : (
                    <textarea
                      ref={taRef}
                      className="queue-textarea"
                      rows={5}
                      value={draftText}
                      onChange={(e) => editActive(e.target.value)}
                      placeholder={`Text ${current.name}…`}
                      aria-label={`Message to ${current.name}`}
                      disabled={sending}
                    />
                  )}
                  {!drafting && liveOffer != null && (
                    <div className="queue-offer">
                      {`💳 This send files a $${liveOffer} credit (expires in 30 days).`}
                    </div>
                  )}
                  <div className="queue-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => void sendCurrent()}
                      disabled={sending || drafting || !draftText.trim()}
                    >
                      {sending ? "Sending…" : "Send 💬"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => taRef.current?.focus()}
                      disabled={sending || drafting}
                      title="Edit the message — sending always uses the current text"
                    >
                      Edit
                    </button>
                    <span className="spacer" />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={skipCurrent}
                      disabled={sending}
                    >
                      Skip →
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => void snoozeCurrent()}
                      disabled={sending}
                      title="Hide from rebook lists and this queue for 45 days — no touch is logged"
                    >
                      Snooze 45d
                    </button>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  Queue clear — every red and yellow client is either texted, cooling down, or
                  snoozed. 🎉
                </div>
              )}
              <ConfettiBurst burst={confettiBurst} />
            </div>
          )}
        </div>
      </div>

      <div className="home-section">
        <div className="panel">
          <div className="panel-title">Reminders</div>
          {remindersQ.error && (
            <div className="error-banner">Failed to load reminders: {remindersQ.error}</div>
          )}
          {remindersQ.loading && (
            <div className="loading">
              <span className="spinner" /> Loading reminders…
            </div>
          )}
          {!remindersQ.loading && reminders.length === 0 && (
            <div className="empty-state">
              No pending reminders. Add one from any client's page.
            </div>
          )}
          {reminders.length > 0 && (
            <div className="action-list">
              {reminders.map((rem) => {
                const overdue = rem.due_date < today;
                return (
                  <div className={"action-item" + (overdue ? " overdue" : "")} key={rem.id}>
                    <div className="action-main">
                      <div className="action-name">
                        <Link to={`/clients/${rem.client_id}`}>{nameOf(rem.client_id)}</Link>{" "}
                        <span className={overdue ? "small bold" : "muted small"}>
                          · {overdue ? "overdue — " : "due "}
                          {fmtDate(rem.due_date)}
                        </span>
                      </div>
                      <div className="action-reason">{rem.note}</div>
                    </div>
                    <div className="action-buttons">
                      {textButton(rem.client_id)}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void completeReminder(rem, "done")}
                        disabled={busyId === rem.id}
                      >
                        Done
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void completeReminder(rem, "dismissed")}
                        disabled={busyId === rem.id}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="home-section">
        <div className="panel">
          <div className="panel-title">
            Packages
            {homePkgs.length > 0 && (
              <span className="muted"> · {homePkgs.length} active</span>
            )}
          </div>
          {packagesQ.error && (
            <div className="error-banner">Failed to load packages: {packagesQ.error}</div>
          )}
          {packagesQ.loading && (
            <div className="loading">
              <span className="spinner" /> Loading packages…
            </div>
          )}
          {!packagesQ.loading && !packagesQ.error && homePkgs.length === 0 && (
            <div className="empty-state">
              No active packages yet — add one from any client page, or just write it in a note
              (&ldquo;has a 10-pack, 3 left&rdquo;) and the nightly AI will file it.
            </div>
          )}
          {homePkgs.length > 0 && (
            <div className="action-list">
              {homePkgs.map((p) => {
                const low = p.balance <= 1;
                const used = p.drips_total - p.balance;
                const pct =
                  p.drips_total > 0
                    ? Math.max(0, Math.min(100, (p.balance / p.drips_total) * 100))
                    : 0;
                const name = pkgNames[String(p.client_id)] ?? `Client ${String(p.client_id)}`;
                return (
                  <div className={"action-item" + (low ? " pkg-low" : "")} key={String(p.id)}>
                    <div className="action-main">
                      <div className="action-name">
                        <Link to={`/clients/${p.client_id}`}>{name}</Link>{" "}
                        {low && <span className="small bold">· low balance</span>}
                      </div>
                      <div className="action-reason">
                        {p.balance} of {p.drips_total} left
                        {used > 0 ? ` · ${used} used` : ""}
                      </div>
                    </div>
                    <div
                      className="pkg-bar"
                      role="img"
                      aria-label={`${p.balance} of ${p.drips_total} drips left`}
                      title={`${p.balance} of ${p.drips_total} drips left`}
                    >
                      <div className="pkg-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="action-buttons">
                      <MarkReachedOut
                        clientId={p.client_id}
                        clientName={name}
                        onDone={reloadTouchLists}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
